import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { nsOptions } from '../i18n';
import { twoFactorActions } from '../redux/actions';
import LabeledInput from '../components/LabeledInput';
import BackupCodesDisplay from '../components/BackupCodesDisplay';
import PasswordForm from '../components/PasswordForm';
import ErrorUtil from '../utils/ErrorUtil';
import Toast from '../utils/Toast';
import history from '../history';
import twoFactorService from '../services/twoFactorService';
import api from '../api';
import '../assets/css/two-factor.css';

const mapStateToProps = (state, ownProps) => ({
  enabled: state.twoFactor.enabled,
  method: state.twoFactor.method,
  policy: state.twoFactor.policy,
  canDisable: state.twoFactor.canDisable,
  backupCodes: state.twoFactor.backupCodes,
  isLoading: state.twoFactor.isLoading,
  // For admin mode, also get the user being edited
  editedUser: ownProps.user,
  isAdminMode: ownProps.admin,
});

const mapDispatchToProps = (dispatch) => ({
  checkStatus: async () => dispatch(twoFactorActions.checkStatus()),
  disable2FA: async (password) => dispatch(twoFactorActions.disable(password)),
  regenerateBackupCodes: async (password) => dispatch(
    twoFactorActions.regenerateBackupCodes(password),
  ),
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
class SecuritySettings extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    enabled: PropTypes.bool.isRequired,
    method: PropTypes.string,
    policy: PropTypes.string,
    canDisable: PropTypes.bool,
    backupCodes: PropTypes.arrayOf(PropTypes.string),
    isLoading: PropTypes.bool.isRequired,
    checkStatus: PropTypes.func.isRequired,
    disable2FA: PropTypes.func.isRequired,
    regenerateBackupCodes: PropTypes.func.isRequired,
    isAdminMode: PropTypes.bool,
    editedUser: PropTypes.shape(),
    // eslint-disable-next-line react/no-unused-prop-types
    user: PropTypes.shape(),
    userId: PropTypes.number,
    patchUser: PropTypes.func,
  };

  static defaultProps = {
    method: null,
    policy: 'optional',
    canDisable: true,
    backupCodes: null,
    isAdminMode: false,
    editedUser: null,
    user: null,
    userId: null,
    patchUser: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      showDisableModal: false,
      showRegenerateModal: false,
      showBackupCodesModal: false,
      adminUserStatus: null, // For admin mode
      adminUserPolicy: null, // Policy for admin mode
      isSavingPolicy: false,
      trustedDevices: [],
      isLoadingDevices: false,
      showRevokeAllModal: false,
    };
    this.disablePasswordInput = null;
    this.regeneratePasswordInput = null;
  }

  async componentDidMount() {
    const { checkStatus, isAdminMode, editedUser } = this.props;

    // En mode admin, on ne charge pas le statut via l'API Redux
    // On utilise les données de l'utilisateur édité
    if (isAdminMode && editedUser) {
      this.setState({
        adminUserStatus: {
          enabled: editedUser.two_factor_enabled || false,
          method: editedUser.two_factor_enabled ? 'totp' : null,
        },
        adminUserPolicy: editedUser.two_factor_policy || 'optional',
      });
      return;
    }

    // En mode utilisateur normal, on charge le statut via Redux
    try {
      await checkStatus();
    } catch (error) {
      console.warn('2FA status check failed:', error);
    }

    // Load trusted devices (only in user mode, not admin)
    if (!isAdminMode) {
      await this.loadTrustedDevices();
    }
  }

  get2FAStatus = () => {
    const { isAdminMode, enabled, method } = this.props;
    const { adminUserStatus } = this.state;

    if (isAdminMode && adminUserStatus) {
      return adminUserStatus;
    }

    return { enabled, method };
  };

  getSubtitleText = (isAdminMode, show2FASection) => {
    if (isAdminMode) {
      return 'Gérez la politique de sécurité pour cet utilisateur.';
    }
    if (show2FASection) {
      return 'Gérez la sécurité de votre compte : mot de passe et authentification à deux facteurs.';
    }
    return 'Gérez la sécurité de votre compte.';
  };

  handlePolicyChange = async (newPolicy) => {
    const { userId, editedUser, patchUser } = this.props;
    const targetUserId = userId || editedUser?.id;

    if (!targetUserId) {
      Toast.error(this.props, 'error:generic', 3000);
      return;
    }

    this.setState({ isSavingPolicy: true });

    try {
      // Use patchUser to update Redux store (keeps user data in sync)
      if (patchUser) {
        await patchUser(targetUserId, { two_factor_policy: newPolicy });
      } else {
        // Fallback to direct API call if patchUser not available
        await api.partial_update('users', targetUserId, { two_factor_policy: newPolicy }, { admin: true });
      }

      this.setState({
        adminUserPolicy: newPolicy,
        isSavingPolicy: false,
      });

      Toast.success(this.props, 'Politique 2FA mise à jour', 2000);
    } catch (error) {
      this.setState({ isSavingPolicy: false });
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  toggleDisableModal = () => {
    this.setState((prevState) => ({
      showDisableModal: !prevState.showDisableModal,
    }));
  };

  toggleRegenerateModal = () => {
    this.setState((prevState) => ({
      showRegenerateModal: !prevState.showRegenerateModal,
    }));
  };

  toggleBackupCodesModal = () => {
    this.setState((prevState) => ({
      showBackupCodesModal: !prevState.showBackupCodesModal,
    }));
  };

  handleEnable = () => {
    history.push('/dashboard/settings/security/2fa/setup');
  };

  handleAdminDisable = async () => {
    const { userId, editedUser } = this.props;
    const targetUserId = userId || editedUser?.id;

    if (!targetUserId) {
      Toast.error(this.props, 'error:generic', 3000);
      return;
    }

    try {
      // Appel API admin pour désactiver le 2FA d'un utilisateur
      // Le paramètre admin=true est obligatoire pour être reconnu comme admin
      await twoFactorService.adminDisable(targetUserId);

      // Mise à jour du state local
      this.setState({
        adminUserStatus: {
          enabled: false,
          method: null,
        },
      });

      Toast.success(this.props, 'twoFactor:manage.disable-modal.success', 3000);
      this.toggleDisableModal();
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  // eslint-disable-next-line consistent-return
  handleDisable = async () => {
    const { disable2FA, isAdminMode } = this.props;

    // Mode admin : pas besoin de mot de passe
    if (isAdminMode) {
      return this.handleAdminDisable();
    }

    // Mode utilisateur normal : besoin du mot de passe
    if (!this.disablePasswordInput || !this.disablePasswordInput.value) {
      // eslint-disable-next-line consistent-return
      return;
    }

    const password = this.disablePasswordInput.value;

    try {
      await disable2FA(password);
      Toast.success(this.props, 'twoFactor:manage.disable-modal.success', 3000);
      this.toggleDisableModal();

      // Rediriger vers la page de configuration obligatoire du 2FA
      setTimeout(() => {
        history.push('/dashboard/2fa-required');
      }, 500);
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  handleRegenerate = async () => {
    const { regenerateBackupCodes } = this.props;

    if (!this.regeneratePasswordInput || !this.regeneratePasswordInput.value) {
      return;
    }

    const password = this.regeneratePasswordInput.value;

    try {
      await regenerateBackupCodes(password);
      Toast.success(this.props, 'twoFactor:manage.regenerate-modal.success', 3000);
      this.toggleRegenerateModal();
      // Show the backup codes
      this.setState({ showBackupCodesModal: true });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  loadTrustedDevices = async () => {
    this.setState({ isLoadingDevices: true });
    try {
      const devices = await api.listTrustedDevices();
      this.setState({ trustedDevices: devices, isLoadingDevices: false });
    } catch (error) {
      console.warn('Failed to load trusted devices:', error);
      this.setState({ isLoadingDevices: false });
    }
  };

  handleRevokeDevice = async (deviceId) => {
    const { t } = this.props;
    try {
      await api.revokeTrustedDevice(deviceId);
      Toast.success(this.props, t('twoFactor:trusted-devices.revoke-success'), 2000);
      await this.loadTrustedDevices();
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  handleRevokeAllDevices = async () => {
    const { t } = this.props;
    try {
      await api.revokeAllTrustedDevices();
      Toast.success(this.props, t('twoFactor:trusted-devices.revoke-all-success'), 2000);
      this.toggleRevokeAllModal();
      await this.loadTrustedDevices();
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  toggleRevokeAllModal = () => {
    this.setState((prevState) => ({
      showRevokeAllModal: !prevState.showRevokeAllModal,
    }));
  };

  renderPasswordSection = () => {
    const { t, isAdminMode } = this.props;

    if (isAdminMode) {
      return null;
    }

    return (
      <div className="row field-row">
        <div className="col-12 col-sm-12 col-md-4 col-lg-3 col-xl-2">
          <label className="field-label">
            {t('user:password')}
          </label>
        </div>
        <div className="col-12 col-sm-12 col-md-6">
          <PasswordForm editionMode {...this.props} />
        </div>
      </div>
    );
  };

  renderAdminPolicySection = () => {
    const { isAdminMode, editedUser } = this.props;
    const { adminUserPolicy, isSavingPolicy } = this.state;

    if (!isAdminMode) {
      return null;
    }

    const userName = editedUser ? `${editedUser.first_name} ${editedUser.last_name}` : '';
    const currentPolicy = adminUserPolicy || 'optional';

    const policyOptions = [
      {
        value: 'disabled',
        label: 'Désactivé',
        icon: 'ban',
        description: 'La double authentification n\'est pas proposée',
      },
      {
        value: 'optional',
        label: 'Optionnel',
        icon: 'user',
        description: 'L\'utilisateur peut activer ou désactiver le 2FA',
      },
      {
        value: 'required',
        label: 'Obligatoire',
        icon: 'lock',
        description: 'L\'utilisateur doit obligatoirement activer le 2FA',
      },
    ];

    return (
      <div className="row field-row">
        <div className="col-12 col-sm-12 col-md-4 col-lg-3 col-xl-2">
          <label className="field-label">
            Authentification à deux facteurs
          </label>
        </div>
        <div className="col-12 col-sm-12 col-md-6">
          {/* Zone Admin - Politique de sécurité */}
          <div
            style={{
              background: 'rgb(255, 255, 255)',
              borderRadius: '8px',
              padding: '1.25rem',
              border: '1px solid rgb(222, 226, 230)',
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon
                icon={['far', 'shield-check']}
                className="mr-2 text-primary"
                style={{ fontSize: '1.1rem' }}
              />
              <strong>Politique de sécurité</strong>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
              {'Définissez la politique d\'accès pour '}
              {userName}
              {' :'}
            </p>

            {/* Boutons de sélection */}
            <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
              {policyOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`btn ${currentPolicy === option.value ? 'btn-newblue-1' : 'btn-outline-secondary'}`}
                  onClick={() => this.handlePolicyChange(option.value)}
                  disabled={isSavingPolicy}
                  style={{
                    minWidth: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {isSavingPolicy && currentPolicy === option.value ? (
                    <FontAwesomeIcon icon={['fal', 'spinner']} spin />
                  ) : (
                    <FontAwesomeIcon icon={['fal', option.icon]} />
                  )}
                  {option.label}
                </button>
              ))}
            </div>

            {/* Description de la politique sélectionnée */}
            <div className="mt-3 text-muted" style={{ fontSize: '0.85rem' }}>
              <FontAwesomeIcon icon={['fas', 'info-circle']} className="mr-2" />
              {policyOptions.find((o) => o.value === currentPolicy)?.description}
            </div>
          </div>
        </div>
      </div>
    );
  };

  render2FASection = () => {
    const { t, isLoading, isAdminMode } = this.props;
    const { enabled, method } = this.get2FAStatus();

    return (
      <>
        <div className="row field-row">
          <div className="col-12 col-sm-12 col-md-4 col-lg-3 col-xl-2">
            <label className="field-label">
              Authentification à deux facteurs
            </label>
          </div>
          <div className="col-12 col-sm-12 col-md-6">
            {/* Zone visuelle délimitée */}
            <div
              style={{
                background: '#ffffff',
                borderRadius: '8px',
                padding: '1.25rem',
                border: '1px solid #dee2e6',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              {/* Statut avec icône */}
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon
                  icon={['far', 'shield-check']}
                  className={`mr-2 ${enabled ? 'text-success' : 'text-muted'}`}
                  style={{ fontSize: '1.25rem' }}
                />
                <div>
                  <span
                    className={`two-factor-status-badge ${enabled ? 'enabled' : 'disabled'}`}
                  >
                    {enabled
                      ? t('twoFactor:manage.status-section.enabled-badge')
                      : t('twoFactor:manage.status-section.disabled-badge')}
                  </span>
                  {enabled && method && (
                    <div className="text-muted mt-1">
                      <small>
                        {t('twoFactor:manage.status-section.method')}
                        {' '}
                        {t('twoFactor:status.method-totp')}
                      </small>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {!enabled && (
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                  <FontAwesomeIcon icon={['fas', 'info-circle']} className="mr-2" />
                  Ajoutez une couche de sécurité supplémentaire en exigeant un code à 6 chiffres
                  lors de la connexion.
                </p>
              )}

              {/* Actions - 2FA non activé */}
              {!enabled && !isAdminMode && (
                <button
                  type="button"
                  className="btn btn-newturquoise-1"
                  onClick={this.handleEnable}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={['far', 'shield-check']} className="mr-2" />
                  {t('twoFactor:manage.actions.enable')}
                </button>
              )}

              {/* Actions - User mode, 2FA enabled, can disable */}
              {!isAdminMode && enabled && this.props.canDisable && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={this.toggleDisableModal}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={['fal', 'ban']} className="mr-2" />
                  {t('twoFactor:manage.actions.disable')}
                </button>
              )}

              {/* Info - User mode, 2FA enabled, cannot disable (policy = required) */}
              {!isAdminMode && enabled && !this.props.canDisable && (
                <div className="alert alert-info mb-0 mt-3" style={{ fontSize: '0.875rem' }}>
                  <FontAwesomeIcon icon={['fal', 'lock']} className="mr-2" />
                  <small>
                    L&apos;authentification à deux facteurs est obligatoire pour votre compte
                    et ne peut pas être désactivée.
                  </small>
                </div>
              )}
            </div>

            {/* Zone des codes de secours (2FA activé) - Séparée visuellement */}
            {enabled && !isAdminMode && (
              <div
                className="mt-3"
                style={{
                  background: '#fff3cd',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #ffc107',
                }}
              >
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon
                    icon={['fal', 'key']}
                    className="text-warning mr-2 mt-1"
                    style={{ fontSize: '1.1rem' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong className="d-block mb-2" style={{ fontSize: '0.9rem' }}>
                      Codes de secours
                    </strong>
                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                      Les codes de secours permettent de vous connecter si vous n&apos;avez pas
                      accès à votre moyen d&apos;authentification.
                    </p>
                    <a
                      href="#"
                      className="classical-link font-weight-semibold"
                      style={{ fontSize: '0.875rem' }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading) {
                          this.toggleRegenerateModal();
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={['fal', 'sync']} className="mr-2" />
                      Régénérer des codes de secours
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  renderTrustedDevicesSection = () => {
    const { isAdminMode, t } = this.props;
    const { enabled } = this.get2FAStatus();
    const { trustedDevices, isLoadingDevices } = this.state;

    // Only show trusted devices section if 2FA is enabled and not in admin mode
    if (!enabled || isAdminMode) {
      return null;
    }

    return (
      <div className="row field-row">
        <div className="col-12 col-sm-12 col-md-4 col-lg-3 col-xl-2">
          <label className="field-label">
            {t('twoFactor:trusted-devices.title')}
          </label>
        </div>
        <div className="col-12 col-sm-12 col-md-6">
          <div
            style={{
              background: '#ffffff',
              borderRadius: '8px',
              padding: '1.25rem',
              border: '1px solid #dee2e6',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon
                icon={['far', 'shield-check']}
                className="mr-2 text-success"
                style={{ fontSize: '1.25rem' }}
              />
              <div>
                <strong>{t('twoFactor:trusted-devices.title')}</strong>
                <div className="text-muted mt-1">
                  <small>
                    {t('twoFactor:trusted-devices.description')}
                  </small>
                </div>
              </div>
            </div>

            {isLoadingDevices && (
              <div className="text-center py-3">
                <FontAwesomeIcon icon={['fal', 'spinner']} spin className="mr-2" />
                {t('common:loading')}
              </div>
            )}

            {!isLoadingDevices && trustedDevices.length === 0 && (
              <div className="text-muted text-center py-3">
                {t('twoFactor:trusted-devices.no-devices')}
              </div>
            )}

            {!isLoadingDevices && trustedDevices.length > 0 && (
              <>
                <div className="list-group mb-3">
                  {trustedDevices.map((device) => (
                    <div
                      key={device.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                      style={{ border: '1px solid #dee2e6', borderRadius: '4px', marginBottom: '0.5rem' }}
                    >
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={['far', 'check-square']}
                            className="mr-2 text-muted"
                          />
                          <strong>{device.device_name || t('twoFactor:trusted-devices.unnamed-device')}</strong>
                        </div>
                        <small className="text-muted d-block mt-1">
                          <FontAwesomeIcon icon={['fal', 'clock']} className="mr-1" />
                          {t('twoFactor:trusted-devices.last-used')}
                          :
                          {' '}
                          {new Date(device.last_used).toLocaleDateString('fr-FR')}
                        </small>
                        <small className="text-muted d-block">
                          <FontAwesomeIcon icon={['far', 'calendar-alt']} className="mr-1" />
                          {t('twoFactor:trusted-devices.expires-on')}
                          :
                          {' '}
                          {new Date(device.expires_at).toLocaleDateString('fr-FR')}
                        </small>
                        {device.is_expired && (
                          <span className="badge badge-danger mt-1">{t('common:expired')}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => this.handleRevokeDevice(device.id)}
                        title={t('twoFactor:trusted-devices.revoke-device')}
                      >
                        <FontAwesomeIcon icon={['fal', 'trash-alt']} />
                      </button>
                    </div>
                  ))}
                </div>
                {trustedDevices.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={this.toggleRevokeAllModal}
                  >
                    <FontAwesomeIcon icon={['fal', 'ban']} className="mr-2" />
                    {t('twoFactor:trusted-devices.revoke-all')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderRevokeAllModal = () => {
    const { t } = this.props;
    const { showRevokeAllModal } = this.state;

    return (
      <Modal isOpen={showRevokeAllModal} toggle={this.toggleRevokeAllModal}>
        <ModalHeader toggle={this.toggleRevokeAllModal}>
          {t('twoFactor:trusted-devices.revoke-all-confirm-title')}
        </ModalHeader>
        <ModalBody>
          <div className="two-factor-modal-warning">
            <span role="img" aria-label="warning">⚠️</span>
            {' '}
            {t('common:caution')}
          </div>
          <p className="mt-3">
            {t('twoFactor:trusted-devices.revoke-all-confirm-message')}
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={this.toggleRevokeAllModal}
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={this.handleRevokeAllDevices}
          >
            <FontAwesomeIcon icon={['fal', 'ban']} className="mr-2" />
            {t('twoFactor:trusted-devices.revoke-all-button')}
          </button>
        </ModalFooter>
      </Modal>
    );
  };

  renderDisableModal = () => {
    const {
      t, isLoading, isAdminMode, editedUser,
    } = this.props;
    const { showDisableModal } = this.state;
    const userName = editedUser ? `${editedUser.first_name} ${editedUser.last_name}` : '';

    return (
      <Modal isOpen={showDisableModal} toggle={this.toggleDisableModal}>
        <ModalHeader toggle={this.toggleDisableModal}>
          {isAdminMode
            ? `Désactiver le 2FA de ${userName}`
            : t('twoFactor:manage.disable-modal.title')}
        </ModalHeader>
        <ModalBody>
          {isAdminMode ? (
            <>
              <div className="two-factor-modal-warning">
                <strong>Attention</strong>
                <p className="mb-0 mt-2">
                  Cette action désactivera l&apos;authentification à deux facteurs pour
                  {' '}
                  {userName}
                  .
                </p>
              </div>
              <p className="mt-3">
                L&apos;utilisateur devra reconfigurer son authentification à deux facteurs
                lors de sa prochaine connexion s&apos;il souhaite réactiver cette fonctionnalité.
              </p>
            </>
          ) : (
            <>
              <div className="two-factor-modal-warning">
                {t('twoFactor:manage.disable-modal.warning')}
              </div>
              <p>{t('twoFactor:manage.disable-modal.description')}</p>
              <LabeledInput
                type="password"
                name="password"
                label={t('twoFactor:manage.disable-modal.password-placeholder')}
                placeholder={t('twoFactor:manage.disable-modal.password-placeholder')}
                required
                onValidChange={(e) => {
                  this.disablePasswordInput = e.target;
                }}
                autoFocus
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={this.toggleDisableModal}
            disabled={isLoading}
          >
            {t('twoFactor:manage.disable-modal.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={this.handleDisable}
            disabled={isLoading}
          >
            {(() => {
              if (isLoading) {
                return (
                  <>
                    <FontAwesomeIcon icon={['fal', 'spinner']} spin className="mr-2" />
                    {isAdminMode ? 'Désactivation...' : 'Processing...'}
                  </>
                );
              }
              if (isAdminMode) {
                return 'Désactiver le 2FA';
              }
              return t('twoFactor:manage.disable-modal.confirm');
            })()}
          </button>
        </ModalFooter>
      </Modal>
    );
  };

  renderRegenerateModal = () => {
    const { t, isLoading } = this.props;
    const { showRegenerateModal } = this.state;

    return (
      <Modal isOpen={showRegenerateModal} toggle={this.toggleRegenerateModal}>
        <ModalHeader toggle={this.toggleRegenerateModal}>
          {t('twoFactor:manage.regenerate-modal.title')}
        </ModalHeader>
        <ModalBody>
          <div className="two-factor-modal-warning">
            {t('twoFactor:manage.regenerate-modal.warning')}
          </div>
          <p>{t('twoFactor:manage.regenerate-modal.description')}</p>
          <LabeledInput
            type="password"
            name="password"
            label={t('twoFactor:manage.regenerate-modal.password-placeholder')}
            placeholder={t('twoFactor:manage.regenerate-modal.password-placeholder')}
            required
            onValidChange={(e) => {
              this.regeneratePasswordInput = e.target;
            }}
            autoFocus
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={this.toggleRegenerateModal}
            disabled={isLoading}
          >
            {t('twoFactor:manage.regenerate-modal.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-newturquoise-1"
            onClick={this.handleRegenerate}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={['fal', 'spinner']} spin className="mr-2" />
                Processing...
              </>
            ) : (
              t('twoFactor:manage.regenerate-modal.confirm')
            )}
          </button>
        </ModalFooter>
      </Modal>
    );
  };

  renderBackupCodesModal = () => {
    const { t, backupCodes } = this.props;
    const { showBackupCodesModal } = this.state;

    if (!backupCodes) return null;

    return (
      <Modal
        isOpen={showBackupCodesModal}
        toggle={this.toggleBackupCodesModal}
        size="lg"
      >
        <ModalHeader toggle={this.toggleBackupCodesModal}>
          {t('twoFactor:backup-codes.title')}
        </ModalHeader>
        <ModalBody>
          <BackupCodesDisplay codes={backupCodes} showActions />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-newturquoise-1"
            onClick={this.toggleBackupCodesModal}
          >
            {t('twoFactor:button.close')}
          </button>
        </ModalFooter>
      </Modal>
    );
  };

  render() {
    const { t, policy, isAdminMode } = this.props;

    // Hide 2FA section if policy is 'disabled' (only for user mode, admin can always see)
    const show2FASection = isAdminMode || policy !== 'disabled';

    return (
      <div>
        <h4 className="font-weight-normal mb-3">
          {t('user:nav.security')}
        </h4>
        <div className="py-3 account-tab-subtitle">
          {this.getSubtitleText(isAdminMode, show2FASection)}
        </div>
        <div className="new-settings inclusion-settings">
          {this.renderPasswordSection()}
          {isAdminMode
            ? this.renderAdminPolicySection()
            : (show2FASection && this.render2FASection())}
          {!isAdminMode && show2FASection && this.renderTrustedDevicesSection()}
        </div>

        {this.renderDisableModal()}
        {this.renderRegenerateModal()}
        {this.renderBackupCodesModal()}
        {this.renderRevokeAllModal()}
      </div>
    );
  }
}

export default SecuritySettings;
